import React from "react";
import { GripVertical } from "lucide-react";

export function arrayMove(items, fromIndex, toIndex) {
  const next = [...items];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
}

export function SortableRowHandle({ handleProps, title = "Arrastra para reordenar" }) {
  return (
    <span
      {...handleProps}
      role="button"
      aria-label={title}
      title={title}
      className="inline-flex cursor-grab touch-none text-gray-400 hover:text-gray-600 active:cursor-grabbing"
      onClick={(event) => event.stopPropagation()}
    >
      <GripVertical className="h-4 w-4" />
    </span>
  );
}

export function useSortableRows({ items, onReorder, disabled = false, getId = (item) => item.id }) {
  const [draggingId, setDraggingId] = React.useState(null);
  const [overId, setOverId] = React.useState(null);
  const armedRef = React.useRef(false);
  const draggingIdRef = React.useRef(null);
  const didDragRef = React.useRef(false);
  const itemsRef = React.useRef(items);
  const onReorderRef = React.useRef(onReorder);

  itemsRef.current = items;
  onReorderRef.current = onReorder;

  const resetDrag = React.useCallback(() => {
    armedRef.current = false;
    draggingIdRef.current = null;
    setDraggingId(null);
    setOverId(null);
  }, []);

  const getHandleProps = React.useCallback(
    () => ({
      onPointerDown: () => {
        if (!disabled) armedRef.current = true;
      },
    }),
    [disabled]
  );

  const getRowProps = React.useCallback(
    (item) => {
      const id = getId(item);
      return {
        draggable: !disabled,
        onDragStart: (event) => {
          if (disabled || !armedRef.current) {
            event.preventDefault();
            return;
          }
          didDragRef.current = true;
          draggingIdRef.current = id;
          setDraggingId(id);
          event.dataTransfer.effectAllowed = "move";
          event.dataTransfer.setData("text/plain", String(id));
        },
        onDragOver: (event) => {
          if (disabled || !draggingIdRef.current) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = "move";
          setOverId((current) => (current === id ? current : id));
        },
        onDrop: (event) => {
          event.preventDefault();
          if (disabled) {
            resetDrag();
            return;
          }
          const currentItems = itemsRef.current || [];
          const fromId = event.dataTransfer.getData("text/plain") || draggingIdRef.current;
          const fromIndex = currentItems.findIndex((row) => String(getId(row)) === String(fromId));
          const toIndex = currentItems.findIndex((row) => String(getId(row)) === String(id));
          if (fromIndex >= 0 && toIndex >= 0 && fromIndex !== toIndex) {
            onReorderRef.current?.(arrayMove(currentItems, fromIndex, toIndex));
          }
          resetDrag();
        },
        onDragEnd: resetDrag,
      };
    },
    [disabled, getId, resetDrag]
  );

  const consumeClickIfDragged = React.useCallback(() => {
    if (!didDragRef.current) return false;
    didDragRef.current = false;
    return true;
  }, []);

  const isDragging = React.useCallback((item) => draggingId === getId(item), [draggingId, getId]);
  const isOver = React.useCallback(
    (item) => Boolean(draggingId) && overId === getId(item) && draggingId !== getId(item),
    [draggingId, getId, overId]
  );

  return {
    getHandleProps,
    getRowProps,
    consumeClickIfDragged,
    isDragging,
    isOver,
  };
}
